import React, { useState } from 'react'
import Modal from 'components/modal'
import Button from 'components/button'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { useAuth } from 'hooks/auth'
import { IOrganisation, ITeamsListResponse } from 'types'
import StatsCard from 'components/statsCard'
import Input from 'components/input'
import UserSelect, { SelectedCount } from 'components/userSelect'

interface CreateTeamModalProps {
  open: boolean
  onClose: () => void
  organisation: IOrganisation
  teamsData?: ITeamsListResponse
  onCreated: () => void
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({ open, onClose, organisation, teamsData, onCreated }) => {
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const { refreshUser } = useAuth()

  const [name, setName] = useState<string>('')
  const [seatLimit, setSeatLimit] = useState<number>(1)
  const [selectedManagerEmails, setSelectedManagerEmails] = useState<string[]>([])
  const [selectedMemberEmails, setSelectedMemberEmails] = useState<string[]>([])
  const [externalEmails, setExternalEmails] = useState<string[]>([])
  const [emailInput, setEmailInput] = useState<string>('')
  const [externalUsersOpen, setExternalUsersOpen] = useState<boolean>(false)
  const [saving, setSaving] = useState<boolean>(false)

  const defaultTeamSeats = teamsData?.defaultSeats ?? 0
  const maxSeatLimit = organisation.multiYearContract ? Infinity : defaultTeamSeats

  const orgUsers = organisation.users.map((u) => ({
    publicId: u.user.email,
    name: u.user.name || u.user.email,
    email: u.user.email,
    createdAt: u.user.createdAt
  }))

  const toggleManager = (email: string) => {
    setSelectedManagerEmails((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]))
  }

  const toggleMember = (email: string) => {
    setSelectedMemberEmails((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]))
  }

  const addExternalEmail = () => {
    const emails = emailInput
      .split(',')
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e))
    if (emails.length === 0) return
    setExternalEmails((prev) => [...prev, ...emails.filter((e) => !prev.includes(e))])
    setEmailInput('')
  }

  const removeExternalEmail = (email: string) => {
    setExternalEmails((prev) => prev.filter((e) => e !== email))
  }

  const canSave =
    name.trim().length > 0 && seatLimit >= 1 && seatLimit <= maxSeatLimit && selectedManagerEmails.length > 0

  const handleSave = () => {
    if (!canSave) return
    setSaving(true)
    const memberEmails = [...selectedMemberEmails, ...externalEmails]
    api
      .createOrganisationTeam(organisation.publicId, {
        name: name.trim(),
        seatLimit,
        managerEmails: selectedManagerEmails,
        memberEmails: memberEmails.length > 0 ? memberEmails : undefined
      })
      .then(() => {
        setSnackbarMessage(`Team "${name.trim()}" created`)
        onCreated()
        refreshUser()
        closeModal()
      })
      .catch(displayErrorMessage)
      .finally(() => setSaving(false))
  }

  const closeModal = () => {
    setName('')
    setSeatLimit(1)
    setSelectedManagerEmails([])
    setSelectedMemberEmails([])
    setExternalEmails([])
    setEmailInput('')
    setExternalUsersOpen(false)
    onClose()
  }

  return (
    <Modal id="create-team" title="Create Team" open={open} setOpen={closeModal}>
      {/* Stats */}
      {teamsData && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <StatsCard label="Total Seats" value={teamsData.totalSeats} className="items-center" />
          <StatsCard label="Allocated" value={teamsData.allocatedSeats} className="items-center" />
          <StatsCard label="Available" value={defaultTeamSeats} className="items-center" />
        </div>
      )}

      {/* Team Name */}
      <div className="mb-4">
        <label htmlFor="create-team-name" className="block text-sm font-semibold mb-1">
          Team Name
        </label>
        <Input
          id="create-team-name"
          type="text"
          placeholder="Enter team name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      {/* Seat Limit */}
      <div className="mb-4 inline-flex flex-col size-fit">
        <label htmlFor="create-team-seat-limit" className="block text-sm font-semibold mb-1">
          Seat Limit{' '}
          {maxSeatLimit !== Infinity && (
            <small className="font-normal text-muted">(max: {defaultTeamSeats} available)</small>
          )}
        </label>
        <Input
          id="create-team-seat-limit"
          type="number"
          className="!w-auto"
          min={1}
          max={maxSeatLimit === Infinity ? undefined : maxSeatLimit}
          value={seatLimit}
          onChange={(e) => setSeatLimit(parseInt(e.target.value) || 1)}
        />
        <span className="text-xs text-muted">The maximum number of paid users this team can assign.</span>
      </div>

      {/* Team Managers */}
      <div className="mb-4">
        <span className="block text-sm font-semibold mb-1">
          Team Managers <small className="font-normal text-muted">(one or more)</small>
          <SelectedCount count={selectedManagerEmails.length} />
        </span>
        <UserSelect users={orgUsers} selectedEmails={selectedManagerEmails} onToggle={toggleManager} showToolbar />
        <span className="text-xs text-muted">
          Managers can assign and revoke paid user seats within this team. They do not need Org Admin access.
        </span>
      </div>

      {/* Assign Users (optional) */}
      <div className="mb-5">
        <span className="block text-sm font-semibold mb-1">
          Assign Users <small className="font-normal text-muted">(optional - this can be done later)</small>
          <SelectedCount count={selectedMemberEmails.length} />
        </span>
        <UserSelect users={orgUsers} selectedEmails={selectedMemberEmails} onToggle={toggleMember} showToolbar />
        <span className="text-xs text-muted">Assigning users upgrades them to a paid license.</span>
      </div>

      {/* External Users */}
      <div className="mb-5">
        <div
          role="button"
          tabIndex={0}
          className="!p-0 flex items-center gap-1 text-sm font-semibold mb-1 cursor-pointer"
          onClick={() => setExternalUsersOpen((prev) => !prev)}
        >
          <span className={`transition-transform ${externalUsersOpen ? 'rotate-90' : ''}`}>&#9654;</span>
          External Users
          {externalEmails.length > 0 && <small className="font-normal text-muted">({externalEmails.length})</small>}
        </div>
        {externalUsersOpen && (
          <>
            <span className="text-xs text-muted block mb-2">
              Add users outside of your organisation. External users consume a paid seat.
            </span>
            <div className="flex gap-2 mb-2">
              <Input
                id="create-team-external-email"
                type="email"
                placeholder="Enter email address..."
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addExternalEmail()
                  }
                }}
              />
              <Button internalType="outline" onClick={addExternalEmail}>
                Add
              </Button>
            </div>
            {externalEmails.length > 0 && (
              <UserSelect
                users={externalEmails.map((email) => ({ publicId: email, name: email, email }))}
                onRemove={removeExternalEmail}
                hideSearch
                renderDetail={() => (
                  <span className="text-xs text-blue bg-really-light-blue px-1.5 py-0.5 rounded">External User</span>
                )}
              />
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2">
        <Button internalType="outline" onClick={closeModal}>
          Cancel
        </Button>
        <Button internalType="accept" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? 'Creating...' : 'Create Team'}
        </Button>
      </div>
    </Modal>
  )
}

export default CreateTeamModal
